import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Link } from 'react-router-dom';
import { ArrowLeft, Send, Check, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const labels = {
  en: {
    back: "Back",
    title: "Contact Us",
    subtitle: "Have a question, suggestion, or want to collaborate? Send us a message and we'll get back to you.",
    name: "Your Name",
    email: "Your Email",
    subject: "Subject",
    message: "Your Message",
    send: "Send Message",
    success: "Message sent successfully! We'll get back to you soon.",
    error: "Something went wrong. Please try again.",
  },
  fr: {
    back: "Retour",
    title: "Contactez-nous",
    subtitle: "Vous avez une question, une suggestion ou souhaitez collaborer ? Envoyez-nous un message et nous vous répondrons.",
    name: "Votre Nom",
    email: "Votre Email",
    subject: "Objet",
    message: "Votre Message",
    send: "Envoyer le message",
    success: "Message envoyé avec succès ! Nous vous répondrons bientôt.",
    error: "Un problème est survenu. Veuillez réessayer.",
  },
  es: {
    back: "Volver",
    title: "Contáctenos",
    subtitle: "¿Tiene una pregunta, sugerencia o desea colaborar? Envíenos un mensaje y le responderemos.",
    name: "Su Nombre",
    email: "Su Email",
    subject: "Asunto",
    message: "Su Mensaje",
    send: "Enviar mensaje",
    success: "¡Mensaje enviado con éxito! Le responderemos pronto.",
    error: "Algo salió mal. Por favor intente de nuevo.",
  },
  pt: {
    back: "Voltar",
    title: "Entre em Contato",
    subtitle: "Tem uma pergunta, sugestão ou deseja colaborar? Envie-nos uma mensagem e responderemos em breve.",
    name: "Seu Nome",
    email: "Seu Email",
    subject: "Assunto",
    message: "Sua Mensagem",
    send: "Enviar mensagem",
    success: "Mensagem enviada com sucesso! Responderemos em breve.",
    error: "Algo deu errado. Por favor, tente novamente.",
  },
};

export default function ContactPage() {
  const { language } = useLanguage();
  const t = labels[language] || labels.en;

  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState('idle');

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setStatus('loading');
    try {
      await axios.post(`${API}/contact`, form);
      setStatus('success');
      setForm({ name: '', email: '', subject: '', message: '' });
      setTimeout(() => setStatus('idle'), 5000);
    } catch {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 4000);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="contact-page">
      <div className="container mx-auto px-6 lg:px-12 max-w-2xl py-12 sm:py-20">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm font-medium text-zinc-500 hover:text-zinc-900 transition-colors mb-10"
          data-testid="contact-back-link"
        >
          <ArrowLeft className="w-4 h-4" />
          {t.back}
        </Link>

        <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="contact-title">
          {t.title}
        </h1>
        <p className="text-zinc-600 leading-relaxed mb-10">{t.subtitle}</p>

        <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
          <div>
            <label className="text-sm font-medium text-zinc-700 block mb-1.5">{t.name}</label>
            <Input
              name="name"
              value={form.name}
              onChange={handleChange}
              required
              className="h-11 rounded-xl"
              data-testid="contact-name-input"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-zinc-700 block mb-1.5">{t.email}</label>
            <Input
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              required
              className="h-11 rounded-xl"
              data-testid="contact-email-input"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-zinc-700 block mb-1.5">{t.subject}</label>
            <Input
              name="subject"
              value={form.subject}
              onChange={handleChange}
              className="h-11 rounded-xl"
              data-testid="contact-subject-input"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-zinc-700 block mb-1.5">{t.message}</label>
            <textarea
              name="message"
              value={form.message}
              onChange={handleChange}
              required
              rows={5}
              className="flex w-full rounded-xl border border-input bg-transparent px-3 py-2 text-base shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none"
              data-testid="contact-message-input"
            />
          </div>

          <Button
            type="submit"
            disabled={status === 'loading'}
            className="rounded-full bg-[#008751] text-white hover:bg-[#006b40] h-12 px-8 text-base font-semibold flex items-center gap-2 transition-transform hover:-translate-y-0.5 hover:shadow-lg"
            data-testid="contact-submit-btn"
          >
            {status === 'loading' ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : status === 'success' ? (
              <Check className="w-4 h-4" />
            ) : (
              <Send className="w-4 h-4" />
            )}
            {t.send}
          </Button>

          {status === 'success' && (
            <p className="flex items-center gap-2 text-sm text-green-600" data-testid="contact-success-msg">
              <Check className="w-4 h-4" /> {t.success}
            </p>
          )}
          {status === 'error' && (
            <p className="flex items-center gap-2 text-sm text-red-600" data-testid="contact-error-msg">
              <AlertCircle className="w-4 h-4" /> {t.error}
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
